import { ReactElement } from "react";
import { useAuth } from "~contexts/Auth";
import { useStreamContext } from "~contexts/Stream";
import { ROLES } from "./constants";
import { AccessDenied } from "~components/Pages";

interface RouteGuardProps {
  children: ReactElement;
  screen: string;
  allowedRoles?: ROLES[];
}

const RouteGuard = ({ children, screen, allowedRoles }: RouteGuardProps) => {
  const { user, hasPermission, getAvailableStreams } = useAuth();
  const { currentStream } = useStreamContext();

  // Se não há usuário logado, não deveria chegar aqui (já foi verificado em PrivateRoutes)
  if (!user) {
    return <AccessDenied isSigned={true} />;
  }

  // Home sempre tem acesso, não precisa verificar permissão
  if (screen === "home") {
    return children;
  }

  // Admin sempre tem acesso
  if (user.role === ROLES.admin) {
    return children;
  }

  // Verificar se o usuário tem uma das roles permitidas
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    console.log('[RouteGuard] User role not allowed', {
      userRole: user.role,
      allowedRoles,
      screen,
    });
    return <AccessDenied isSigned={true} />;
  }

  // Se não há stream selecionada, verificar se tem permissão para qualquer stream disponível
  if (!currentStream) {
    const availableStreams = getAvailableStreams();
    
    console.log('[RouteGuard] No currentStream, checking permissions', {
      screen,
      availableStreams,
      userRole: user.role,
    });
    
    // Se não tem nenhuma stream disponível, negar acesso
    if (availableStreams.length === 0) {
      console.log('[RouteGuard] No available streams');
      return <AccessDenied isSigned={true} />;
    }
    
    // Verificar se tem permissão para pelo menos uma stream
    const hasAnyPermission = availableStreams.some((stream) => {
      const hasPerm = hasPermission(stream.id_stream, screen);
      console.log('[RouteGuard] Checking permission', { streamId: stream.id_stream, screen, hasPerm });
      return hasPerm;
    });
    
    console.log('[RouteGuard] Has any permission?', { hasAnyPermission });
    
    if (!hasAnyPermission) {
      return <AccessDenied isSigned={true} />;
    }
    
    return children;
  }

  // Verificar permissão específica para a stream atual e screen
  const streamId = currentStream.id;
  const hasAccess = hasPermission(streamId, screen);

  console.log('[RouteGuard] Checking permission with currentStream', {
    streamId,
    screen,
    hasAccess,
    userRole: user.role,
  });

  if (!hasAccess) {
    return <AccessDenied isSigned={true} />;
  }

  return children;
};

export default RouteGuard;
